///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2024, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2024 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

export interface RGB {
  red: number;
  green: number;
  blue: number;
}

/**
 * Viewer parameters.
 */
export interface IOptions {
  /**
   * Show WCS
   *
   * @defaultValue true
   */
  showWCS?: boolean;

  /**
   * Enable camera animation
   *
   * @defaultValue true
   */
  cameraAnimation?: boolean;

  /**
   * Enable antialiasing use FXAA
   *
   * @defaultValue true
   */
  antialiasing?: boolean;

  /**
   * Enable ground shadows
   *
   * @defaultValue false
   */
  groundShadow?: boolean;

  /**
   * Enable shadows
   *
   * @defaultValue false
   */
  shadows?: boolean;

  /**
   * Camera speed on X axis
   *
   * @defaultValue 4
   */
  cameraAxisXSpeed?: number;

  /**
   * Camera speed on Y axis
   *
   * @defaultValue 1
   */
  cameraAxisYSpeed?: number;

  /**
   * Ambient occlusion
   *
   * @defaultValue false
   */
  ambientOcclusion?: boolean;

  /**
   * Enable streaming of drawings from the server
   *
   * If streaming is disabled, the file/assembly will be loaded in one go. If streaming is
   * enabled, partial download mode may be enabled as well.
   *
   * @defaultValue true
   */
  enableStreamingMode?: boolean;

  /**
   * Enable partial load mode to be able open large drawing. Only used if streaming is enabled.
   * If partial mode is enabled, then scene graph will be disabled.
   *
   * @defaultValue false
   */
  enablePartialMode?: boolean;

  /**
   * The size of the memory buffer that the Viewer can use for graphics data
   *
   * @defaultValue 3294967296
   */
  memoryLimit?: number;

  /**
   * Cutting plane fill color
   *
   * @defaultValue { red: 0xff, green: 0x98, blue: 0x00 }
   */
  cuttingPlaneFillColor?: RGB;

  /**
   * Outline edges color in RGB format.
   */
  edgesColor?: { r: number; g: number; b: number };

  /**
   * Faces color in the RGB format.
   */
  facesColor?: { r: number; g: number; b: number };

  /**
   * Show or hide edges.
   */
  edgesVisibility?: boolean;

  /**
   * Show edges over drawing.
   */
  edgesOverlap?: boolean;

  /**
   * Show faces over drawing.
   */
  facesOverlap?: boolean;

  /**
   * Faces transparency value from 0 to 255.
   */
  facesTransparancy?: number;

  /**
   * Enable custom highlight settings.
   */
  enableCustomHighlight?: boolean;

  /**
   * Enable or disable scene graph, it increases perfomance improvement, but consumes memory.
   * Large drawings can take up a lot of memory. If sceneGraph enabled, then enablePartialMode
   * will be disabled
   */
  sceneGraph: boolean;

  /**
   * Edge display models. No edges is usefull for less memory consumption: `false` - no edges
   * are displayed, `true` - display isolines.
   */
  edgeModel: boolean;

  /**
   * Reverse the mouse wheel direction for zooming: false - moving the wheel up zooms in,
   * moving down zooms out, `true` - moving the wheel up zooms out, moving down zooms in.
   */
  reverseZoomWheel: boolean;

  /**
   * Enable mouse wheel zooming.
   */
  enableZoomWheel: boolean;

  /**
   * Enable touch gestures. This option will be ignored when enableZoomWheel is disabled, since
   * gestures contains touch zoom.
   */
  enableGestures: boolean;

  /**
   * Default file geometry data type. Can be one of:
   *
   * - `vsfx` - `VSFX` (default), for opening a file in `VisualizeJS` viewer.
   * - `gltf` - `glTF`, for opening a file in `Three.js` viewer.
   */
  geometryType?: string;
}

export function defaultOptions(): IOptions {
  return {
    showWCS: true,
    cameraAnimation: true,
    antialiasing: true,
    groundShadow: false,
    shadows: false,
    cameraAxisXSpeed: 4,
    cameraAxisYSpeed: 1,
    ambientOcclusion: false,
    enableStreamingMode: true,
    enablePartialMode: false,
    memoryLimit: 3294967296,
    cuttingPlaneFillColor: { red: 0xff, green: 0x98, blue: 0x00 },
    edgesColor: { r: 0xff, g: 0x98, b: 0x00 },
    facesColor: { r: 0xff, g: 0x98, b: 0x00 },
    edgesVisibility: true,
    edgesOverlap: true,
    facesOverlap: false,
    facesTransparancy: 200,
    enableCustomHighlight: true,
    sceneGraph: false,
    edgeModel: true,
    reverseZoomWheel: false,
    enableZoomWheel: true,
    enableGestures: true,
    geometryType: "vsfx",
  };
}
